import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFirestore from '../hooks/useFirestore';
import {
  CoinGeckoGlobalData,
  CoinGeckoMarket,
  CoinGeckoResourceState,
  CoinGeckoSearchResult,
  CoinGeckoState,
} from '../types';
import {
  buildMarketsRequestKey,
  buildSearchResourceKey,
  COINGECKO_LAZY_TTL_MS,
  COINGECKO_MARKETS_TTL_MS,
  COINGECKO_SEARCH_TTL_MS,
  normalizeCoinIds,
} from '../utils/coingecko';
import { CORE_GECKO_IDS } from '../utils/marketSymbols';

interface EnsureMarketsOptions {
  force?: boolean;
  track?: boolean;
}

interface EnsureResourceOptions {
  force?: boolean;
}

export interface CoinGeckoContextValue extends CoinGeckoState {
  ensureMarkets: (ids: string[], options?: EnsureMarketsOptions) => Promise<Record<string, CoinGeckoMarket>>;
  refreshMarkets: (ids?: string[]) => Promise<Record<string, CoinGeckoMarket>>;
  ensureResource: (resourceKey: string, options?: EnsureResourceOptions) => Promise<unknown>;
  searchCoins: (query: string) => Promise<CoinGeckoSearchResult[]>;
  getMarketById: (id: string) => CoinGeckoMarket | null;
}

export const CoinGeckoContext = createContext<CoinGeckoContextValue | null>(null);

type UnknownResourceMap = Record<string, CoinGeckoResourceState<unknown>>;
type MarketMetaMap = Record<string, CoinGeckoResourceState<string[]>>;

function createResourceState<T>(data: T | null = null): CoinGeckoResourceState<T> {
  return {
    data,
    status: data === null ? 'idle' : 'success',
    error: null,
    lastFetchedAt: null,
    expiresAt: null,
    inFlightKey: null,
  };
}

function isFresh<T>(resource?: CoinGeckoResourceState<T>): boolean {
  return !!resource?.expiresAt && resource.expiresAt > Date.now();
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown CoinGecko error';
}

function normalizeMarket(item: any): CoinGeckoMarket {
  return {
    id: String(item.id ?? ''),
    symbol: String(item.symbol ?? '').toUpperCase(),
    name: String(item.name ?? ''),
    image: String(item.image ?? ''),
    currentPrice: Number(item.current_price ?? 0),
    priceChangePercentage24h: Number(item.price_change_percentage_24h ?? 0),
    marketCap: Number(item.market_cap ?? 0),
    totalVolume: Number(item.total_volume ?? 0),
    sparklineIn7d: Array.isArray(item.sparkline_in_7d?.price)
      ? item.sparkline_in_7d.price.map((value: unknown) => Number(value ?? 0))
      : [],
  };
}

function normalizeSearchResult(item: any): CoinGeckoSearchResult {
  return {
    id: String(item.id ?? ''),
    name: String(item.name ?? ''),
    symbol: String(item.symbol ?? '').toUpperCase(),
    thumb: String(item.thumb ?? ''),
  };
}

async function fetchJson(url: string): Promise<any> {
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('CoinGecko rate limit exceeded');
    }
    throw new Error(`CoinGecko request failed with HTTP ${response.status}`);
  }
  return response.json();
}

export const CoinGeckoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const watchlistIds = useFirestore<string[]>('crypto-missions:watchlistIds', []);
  const [marketsById, setMarketsById] = useState<Record<string, CoinGeckoMarket>>({});
  const [trackedMarketIds, setTrackedMarketIds] = useState<string[]>(CORE_GECKO_IDS);
  const [marketRequestMeta, setMarketRequestMeta] = useState<MarketMetaMap>({});
  const [resources, setResources] = useState<UnknownResourceMap>({
    markets: createResourceState<string[]>(CORE_GECKO_IDS),
    trending: createResourceState<string[]>(),
    global: createResourceState<CoinGeckoGlobalData>(),
  });

  const marketsByIdRef = useRef(marketsById);
  const trackedMarketIdsRef = useRef(trackedMarketIds);
  const marketRequestMetaRef = useRef(marketRequestMeta);
  const resourcesRef = useRef(resources);
  const marketInflightRef = useRef<{ key: string; promise: Promise<Record<string, CoinGeckoMarket>> } | null>(null);
  const resourceInflightRef = useRef<Record<string, Promise<unknown>>>({});

  useEffect(() => {
    marketsByIdRef.current = marketsById;
  }, [marketsById]);

  useEffect(() => {
    trackedMarketIdsRef.current = trackedMarketIds;
  }, [trackedMarketIds]);

  useEffect(() => {
    marketRequestMetaRef.current = marketRequestMeta;
  }, [marketRequestMeta]);

  useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  const mergeTrackedIds = useCallback((ids: string[]): string[] => {
    const nextIds = normalizeCoinIds([...CORE_GECKO_IDS, ...watchlistIds, ...ids]);
    setTrackedMarketIds((prev) => {
      const normalizedPrev = normalizeCoinIds(prev);
      return normalizedPrev.join(',') === nextIds.join(',') ? prev : nextIds;
    });
    return nextIds;
  }, [watchlistIds]);

  useEffect(() => {
    mergeTrackedIds([]);
  }, [mergeTrackedIds, watchlistIds]);

  const getMarketSubset = useCallback((ids: string[]): Record<string, CoinGeckoMarket> => {
    const result: Record<string, CoinGeckoMarket> = {};
    normalizeCoinIds(ids).forEach((id) => {
      const market = marketsByIdRef.current[id];
      if (market) {
        result[id] = market;
      }
    });
    return result;
  }, []);

  const fetchMarkets = useCallback(async (
    ids: string[],
    options: { force?: boolean; syncTrackedResource?: boolean } = {}
  ): Promise<Record<string, CoinGeckoMarket>> => {
    const normalizedIds = normalizeCoinIds(ids);
    if (normalizedIds.length === 0) {
      return {};
    }

    const requestKey = buildMarketsRequestKey(normalizedIds);
    const requestState = marketRequestMetaRef.current[requestKey];
    if (!options.force && isFresh(requestState)) {
      return getMarketSubset(normalizedIds);
    }

    if (marketInflightRef.current?.key === requestKey) {
      return marketInflightRef.current.promise;
    }

    const trackedResourceState = resourcesRef.current.markets as CoinGeckoResourceState<string[]> | undefined;
    setMarketRequestMeta((prev) => ({
      ...prev,
      [requestKey]: {
        ...(prev[requestKey] ?? createResourceState<string[]>(normalizedIds)),
        data: normalizedIds,
        status: 'loading',
        error: null,
        inFlightKey: requestKey,
      },
    }));
    if (options.syncTrackedResource !== false) {
      setResources((prev) => ({
        ...prev,
        markets: {
          ...(trackedResourceState ?? createResourceState<string[]>(trackedMarketIdsRef.current)),
          data: trackedMarketIdsRef.current,
          status: 'loading',
          error: null,
          inFlightKey: requestKey,
        },
      }));
    }

    const promise = (async () => {
      try {
        const data = await fetchJson(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${normalizedIds.join(',')}&order=market_cap_desc&sparkline=true&price_change_percentage=24h`
        );
        const nextMarkets: Record<string, CoinGeckoMarket> = {};
        (Array.isArray(data) ? data : []).forEach((item) => {
          const market = normalizeMarket(item);
          nextMarkets[market.id] = market;
        });
        setMarketsById((prev) => ({ ...prev, ...nextMarkets }));

        const fetchedAt = Date.now();
        const successState: CoinGeckoResourceState<string[]> = {
          data: normalizedIds,
          status: 'success',
          error: null,
          lastFetchedAt: fetchedAt,
          expiresAt: fetchedAt + COINGECKO_MARKETS_TTL_MS,
          inFlightKey: requestKey,
        };
        setMarketRequestMeta((prev) => ({
          ...prev,
          [requestKey]: successState,
        }));
        if (options.syncTrackedResource !== false) {
          setResources((prev) => ({
            ...prev,
            markets: {
              data: trackedMarketIdsRef.current,
              status: 'success',
              error: null,
              lastFetchedAt: fetchedAt,
              expiresAt: fetchedAt + COINGECKO_MARKETS_TTL_MS,
              inFlightKey: requestKey,
            },
          }));
        }

        return nextMarkets;
      } catch (error) {
        const message = toErrorMessage(error);
        setMarketRequestMeta((prev) => ({
          ...prev,
          [requestKey]: {
            data: normalizedIds,
            status: 'error',
            error: message,
            lastFetchedAt: prev[requestKey]?.lastFetchedAt ?? null,
            expiresAt: prev[requestKey]?.expiresAt ?? null,
            inFlightKey: requestKey,
          },
        }));
        if (options.syncTrackedResource !== false) {
          setResources((prev) => ({
            ...prev,
            markets: {
              data: trackedMarketIdsRef.current,
              status: 'error',
              error: message,
              lastFetchedAt: (prev.markets as CoinGeckoResourceState<string[]> | undefined)?.lastFetchedAt ?? null,
              expiresAt: (prev.markets as CoinGeckoResourceState<string[]> | undefined)?.expiresAt ?? null,
              inFlightKey: requestKey,
            },
          }));
        }
        return getMarketSubset(normalizedIds);
      } finally {
        if (marketInflightRef.current?.key === requestKey) {
          marketInflightRef.current = null;
        }
      }
    })();

    marketInflightRef.current = { key: requestKey, promise };
    return promise;
  }, [getMarketSubset]);

  const ensureMarkets = useCallback(async (
    ids: string[],
    options: EnsureMarketsOptions = {}
  ): Promise<Record<string, CoinGeckoMarket>> => {
    const track = options.track !== false;
    const nextIds = track ? mergeTrackedIds(ids) : normalizeCoinIds(ids);
    return fetchMarkets(nextIds, {
      force: options.force,
      syncTrackedResource: track,
    });
  }, [fetchMarkets, mergeTrackedIds]);

  const refreshMarkets = useCallback(async (ids?: string[]): Promise<Record<string, CoinGeckoMarket>> => {
    const nextIds = ids?.length ? mergeTrackedIds(ids) : trackedMarketIdsRef.current;
    return fetchMarkets(nextIds, { force: true, syncTrackedResource: true });
  }, [fetchMarkets, mergeTrackedIds]);

  const fetchTrending = useCallback(async (force = false): Promise<string[]> => {
    const resourceKey = 'trending';
    const current = resourcesRef.current[resourceKey] as CoinGeckoResourceState<string[]> | undefined;
    if (!force && isFresh(current)) {
      return current?.data ?? [];
    }
    if (resourceInflightRef.current[resourceKey]) {
      await resourceInflightRef.current[resourceKey];
      return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<string[]> | undefined)?.data ?? []);
    }

    setResources((prev) => ({
      ...prev,
      [resourceKey]: {
        ...((prev[resourceKey] as CoinGeckoResourceState<string[]> | undefined) ?? createResourceState<string[]>()),
        status: 'loading',
        error: null,
        inFlightKey: resourceKey,
      },
    }));

    const promise = (async () => {
      try {
        const data = await fetchJson('https://api.coingecko.com/api/v3/search/trending');
        const ids = Array.isArray(data?.coins)
          ? data.coins.slice(0, 7).map((coin: any) => String(coin?.item?.id ?? '')).filter(Boolean)
          : [];
        const fetchedAt = Date.now();
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: ids,
            status: 'success',
            error: null,
            lastFetchedAt: fetchedAt,
            expiresAt: fetchedAt + COINGECKO_LAZY_TTL_MS,
            inFlightKey: resourceKey,
          },
        }));
        await ensureMarkets(ids, { track: false, force });
        return ids;
      } catch (error) {
        const message = toErrorMessage(error);
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: (prev[resourceKey] as CoinGeckoResourceState<string[]> | undefined)?.data ?? [],
            status: 'error',
            error: message,
            lastFetchedAt: (prev[resourceKey] as CoinGeckoResourceState<string[]> | undefined)?.lastFetchedAt ?? null,
            expiresAt: (prev[resourceKey] as CoinGeckoResourceState<string[]> | undefined)?.expiresAt ?? null,
            inFlightKey: resourceKey,
          },
        }));
        return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<string[]> | undefined)?.data ?? []);
      } finally {
        delete resourceInflightRef.current[resourceKey];
      }
    })();

    resourceInflightRef.current[resourceKey] = promise;
    return promise;
  }, [ensureMarkets]);

  const fetchGlobal = useCallback(async (force = false): Promise<CoinGeckoGlobalData | null> => {
    const resourceKey = 'global';
    const current = resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined;
    if (!force && isFresh(current)) {
      return current?.data ?? null;
    }
    if (resourceInflightRef.current[resourceKey]) {
      await resourceInflightRef.current[resourceKey];
      return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.data ?? null);
    }

    setResources((prev) => ({
      ...prev,
      [resourceKey]: {
        ...((prev[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined) ?? createResourceState<CoinGeckoGlobalData>()),
        status: 'loading',
        error: null,
        inFlightKey: resourceKey,
      },
    }));

    const promise = (async () => {
      try {
        const data = await fetchJson('https://api.coingecko.com/api/v3/global');
        const nextData = (data?.data ?? null) as CoinGeckoGlobalData | null;
        const fetchedAt = Date.now();
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: nextData,
            status: 'success',
            error: null,
            lastFetchedAt: fetchedAt,
            expiresAt: fetchedAt + COINGECKO_LAZY_TTL_MS,
            inFlightKey: resourceKey,
          },
        }));
        return nextData;
      } catch (error) {
        const message = toErrorMessage(error);
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.data ?? null,
            status: 'error',
            error: message,
            lastFetchedAt: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.lastFetchedAt ?? null,
            expiresAt: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.expiresAt ?? null,
            inFlightKey: resourceKey,
          },
        }));
        return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined)?.data ?? null);
      } finally {
        delete resourceInflightRef.current[resourceKey];
      }
    })();

    resourceInflightRef.current[resourceKey] = promise;
    return promise;
  }, []);

  const searchCoins = useCallback(async (query: string): Promise<CoinGeckoSearchResult[]> => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return [];
    }

    const resourceKey = buildSearchResourceKey(trimmedQuery);
    const current = resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined;
    if (isFresh(current)) {
      return current?.data ?? [];
    }
    if (resourceInflightRef.current[resourceKey]) {
      await resourceInflightRef.current[resourceKey];
      return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined)?.data ?? []);
    }

    setResources((prev) => ({
      ...prev,
      [resourceKey]: {
        ...((prev[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined) ?? createResourceState<CoinGeckoSearchResult[]>()),
        status: 'loading',
        error: null,
        inFlightKey: resourceKey,
      },
    }));

    const promise = (async () => {
      try {
        const data = await fetchJson(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(trimmedQuery)}`);
        const results = Array.isArray(data?.coins)
          ? data.coins.slice(0, 8).map(normalizeSearchResult)
          : [];
        const fetchedAt = Date.now();
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: results,
            status: 'success',
            error: null,
            lastFetchedAt: fetchedAt,
            expiresAt: fetchedAt + COINGECKO_SEARCH_TTL_MS,
            inFlightKey: resourceKey,
          },
        }));
        return results;
      } catch (error) {
        const message = toErrorMessage(error);
        setResources((prev) => ({
          ...prev,
          [resourceKey]: {
            data: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined)?.data ?? [],
            status: 'error',
            error: message,
            lastFetchedAt: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined)?.lastFetchedAt ?? null,
            expiresAt: (prev[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined)?.expiresAt ?? null,
            inFlightKey: resourceKey,
          },
        }));
        return ((resourcesRef.current[resourceKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined)?.data ?? []);
      } finally {
        delete resourceInflightRef.current[resourceKey];
      }
    })();

    resourceInflightRef.current[resourceKey] = promise;
    return promise;
  }, []);

  const ensureResource = useCallback(async (resourceKey: string, options: EnsureResourceOptions = {}): Promise<unknown> => {
    if (resourceKey === 'trending') {
      return fetchTrending(options.force);
    }
    if (resourceKey === 'global') {
      return fetchGlobal(options.force);
    }
    if (resourceKey.startsWith('search:')) {
      return searchCoins(resourceKey.slice('search:'.length));
    }
    return null;
  }, [fetchGlobal, fetchTrending, searchCoins]);

  const getMarketById = useCallback((id: string): CoinGeckoMarket | null => {
    return marketsByIdRef.current[id.trim().toLowerCase()] ?? null;
  }, []);

  useEffect(() => {
    void fetchMarkets(mergeTrackedIds([]), { syncTrackedResource: true });
  }, [fetchMarkets, mergeTrackedIds]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      void fetchMarkets(trackedMarketIdsRef.current, { syncTrackedResource: true });
    }, COINGECKO_MARKETS_TTL_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchMarkets]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }
      void fetchMarkets(trackedMarketIdsRef.current, { syncTrackedResource: true });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchMarkets]);

  const value = useMemo<CoinGeckoContextValue>(() => ({
    marketsById,
    trackedMarketIds,
    marketRequestMeta,
    resources,
    ensureMarkets,
    refreshMarkets,
    ensureResource,
    searchCoins,
    getMarketById,
  }), [
    ensureMarkets,
    ensureResource,
    getMarketById,
    marketRequestMeta,
    marketsById,
    refreshMarkets,
    resources,
    searchCoins,
    trackedMarketIds,
  ]);

  return (
    <CoinGeckoContext.Provider value={value}>
      {children}
    </CoinGeckoContext.Provider>
  );
};
